<svelte:options runes={true} />

<script lang="ts">
  // #region Imports

  import { listIncludeSymbols } from '@cadet-dsl';
  import type { CommandContext } from '@commands';
  import * as Commands from '@commands';
  import {
    calculateViewportForBounds,
    CommandId,
    CommandManager,
    ToolManager
  } from '@commands';
  import { Doc, Layouter, makeCy } from '@doc';
  import { DEFAULT_MIN_ZOOM, MAX_ZOOM, NOMINAL_ZOOM } from '@env';
  import { HAlignment, Point, VAlignment } from '@geom';
  import { Elements, type NID } from '@graph';
  import type { SpatialIndex } from '@layout';
  import { KeyManager, prefs, TitleEditorState, UndoManager } from '@reactive';
  import { canvasKit } from '@resources';
  import { getElementBounds, TransitionManager } from '@ui';
  import { assertDefined, clamp, expectDefined, reactTo } from '@utils';
  import Cytoscape from 'cytoscape';
  import { ScriptingContext } from '../scripting/ScriptingContext';
  import { setGlobalScriptingEnv } from '../scripting/ScriptingEnv';
  import BarContainer from './BarContainer.svelte';
  import BottomBar from './BottomBar.svelte';
  import DocCanvas from './DocCanvas.svelte';
  import Hud from './Hud.svelte';
  import LeftBar from './LeftBar.svelte';
  import RightBar from './RightBar.svelte';
  import TitleEditor from './TitleEditor.svelte';
  import TopBar from './TopBar.svelte';

  // #endregion

  // #region Constants

  const MAX_SAVE_INTERVAL_SECONDS = 3;

  // #endregion

  // #region State

  // Build a blank DSL that imports all registered system includes (e.g., :INCLUDE prelude)
  function makeBlankDsl(): string {
    const includes = listIncludeSymbols() as string[];
    const includeLines = includes.map((s: string) => `:INCLUDE ${s}`).join('\n');
    return `%CADET-1.0\n\n${includeLines}\n`;
  }

  let doc = $state(new Doc());
  let lastDoc: Doc | undefined;
  let spatialIndex = $state<SpatialIndex | undefined>(undefined);

  let selection = $state(Elements.make());
  let lastSelection = $state(Elements.make());

  let selectedUnitClass = $state<string | undefined>(undefined);
  let selectedGroupClass = $state<string | undefined>(undefined);

  let layouter = $state<Layouter | undefined>(undefined);
  let transitionManager = $state<TransitionManager | undefined>(undefined);

  let undoManager = new UndoManager();
  let commandManager = new CommandManager();
  let keyManager = new KeyManager(commandManager);
  let toolManager = new ToolManager(keyManager);

  let element: HTMLElement;
  let canvasElement = $state<HTMLElement | undefined>(undefined);
  let commandContext: () => CommandContext;
  let cy: Cytoscape.Core | undefined;

  let pan = $state(Point.ZERO);
  let targetPan = $state<Point | undefined>(undefined);

  let maxZoom = $state(MAX_ZOOM);
  let minZoom = $state(1);

  let entryPoints = $state<Map<NID, Point> | undefined>(undefined);

  let titleEditorState = $state(new TitleEditorState());

  let isEditorVisible = $derived(titleEditorState.isVisible);
  let isModal = $derived(spatialIndex === undefined || isEditorVisible);

  let saveInterval: ReturnType<typeof setTimeout> | undefined;

  // #endregion

  // #region Reactive

  function updateMinZoom(): number {
    if (canvasElement === undefined) {
      return DEFAULT_MIN_ZOOM;
    }
    if (!doc.hasLayout) {
      return DEFAULT_MIN_ZOOM;
    }
    const elemBounds = expectDefined(getElementBounds(canvasElement));
    let minZoom = doc.fitToRect(elemBounds)?.zoom ?? DEFAULT_MIN_ZOOM;
    // minZoom = clamp(minZoom, [DEFAULT_MIN_ZOOM, maxZoom]);
    minZoom = Math.min(minZoom, NOMINAL_ZOOM);
    return minZoom;
  }

  $effect(() => {
    minZoom = updateMinZoom();
  });

  // $effect(() => {
  //   if (spatialIndex) {
  //     console.log(`🚩 Spatial index set.`);
  //   } else {
  //     console.log('🚩 Spatial index cleared.');
  //   }
  // });

  let zoom = $state(1);
  let targetZoom = $state<number | undefined>(undefined);

  $effect(() => {
    cy = makeCy(element);
    layouter = new Layouter(cy);
    keyManager.install(window);

    return () => {
      keyManager.uninstall();
      expectDefined(layouter).dispose();
      expectDefined(cy).destroy();
    };
  });

  function canTransition(): boolean {
    return lastDoc !== undefined && lastDoc.hasLayout && doc.hasLayout;
  }

  let lastMinZoom = 0;
  let minZoomIncreased = false;
  $effect(() => {
    reactTo(minZoom);
    if (canTransition()) {
      if (minZoom > lastMinZoom) {
        minZoomIncreased = true;
      }
      lastMinZoom = minZoom;
    }
  });

  $effect(() => {
    const docUpdated =
      lastDoc !== undefined &&
      lastDoc.hasLayout &&
      doc.hasLayout &&
      lastDoc.id !== doc.id;

    if (docUpdated) {
      setNeedsSave();
    }

    let goalZoom = targetZoom ?? zoom;
    if (minZoomIncreased) {
      minZoomIncreased = false;
      if (goalZoom < minZoom) {
        goalZoom = minZoom;
      }
    }

    const goalPan = targetPan ?? pan;

    if (docUpdated || zoom !== goalZoom || !pan.equals(goalPan)) {
      const startDoc = lastDoc ?? doc;
      const startSelection = lastSelection;
      const startPan = pan;
      const startZoom = zoom;

      const endDoc = doc;
      const endSelection = selection;
      const endPan = goalPan;
      const endZoom = goalZoom;

      transitionManager = new TransitionManager(
        startDoc,
        startSelection,
        startPan,
        startZoom,
        endDoc,
        endSelection,
        endPan,
        endZoom,
        prefs.colorMode,
        entryPoints
      );
      spatialIndex = undefined;
    }
    lastDoc = doc;
    lastSelection = selection;
    if (targetPan !== undefined) {
      targetPan = undefined;
    }
    if (targetZoom !== undefined) {
      targetZoom = undefined;
    }
    if (!pan.equals(goalPan)) {
      pan = goalPan;
    }
    if (zoom !== goalZoom) {
      zoom = goalZoom;
    }
  });

  // #endregion

  // #region Save

  function setNeedsSave() {
    if (saveInterval !== undefined) {
      clearTimeout(saveInterval);
    }
    saveInterval = setTimeout(() => {
      const dsl = doc.savePretty({ layoutAtEnd: true });
      console.log(dsl);
      prefs.backupDoc = dsl;
      console.log('👍 Saved.');

      // let svg = exportToSvg(doc, expectDefined(layouter).cache);
      // console.log(svg);
    }, MAX_SAVE_INTERVAL_SECONDS * 1000);
  }

  // #endregion

  // #region Setup

  function assignUp(element: HTMLElement, getCommandContext: () => CommandContext) {
    canvasElement = element;
    commandContext = getCommandContext;
    setup();
  }

  function setup() {
    expectDefined(canvasKit);
    if (cy) {
      assertDefined(layouter);
      // Load document from DSL stored in prefs.backupDoc.
      // If DSL is empty, or if parsing fails (e.g. legacy JSON present),
      // use a fresh blank document so the UI still starts.
      const dsl = prefs.backupDoc;
      if (!dsl) {
        const blank = makeBlankDsl();
        doc = Doc.load(blank, layouter.cache);
      } else {
        try {
          console.log(`Loading DSL from prefs:\n${dsl}`);
          doc = Doc.load(dsl, layouter.cache);
        } catch (err) {
          // Emit a concise diagnostic but keep the app running.
          const message = err instanceof Error ? err.toString() : String(err);
          console.error(
            'CADET-DSL parse failed. Falling back to empty document.\n' + message
          );
          const blank = makeBlankDsl();
          doc = Doc.load(blank, layouter.cache);
        }
      }
      if (!doc.hasLayout) {
        // Fresh document: run initial + incremental layout (no animation yet since lastDoc undefined)
        doc = layouter.runInitialLayout(doc);
        doc = layouter.runIncrementalLayout(doc, doc.bounds);
      }

      // Initialize viewport to center the document and apply appropriate zoom
      if (doc.hasLayout && doc.bounds && canvasElement) {
        const elemBounds = getElementBounds(canvasElement);
        if (elemBounds) {
          const viewportState = calculateViewportForBounds(
            doc.bounds,
            elemBounds,
            MAX_ZOOM,
            DEFAULT_MIN_ZOOM
          );

          if (viewportState) {
            // Clamp zoom to between DEFAULT_MIN_ZOOM and 1.0 (NOMINAL_ZOOM)
            const clampedZoom = clamp(viewportState.zoom, [
              DEFAULT_MIN_ZOOM,
              NOMINAL_ZOOM
            ]);

            // If zoom was clamped, recalculate pan to keep document centered
            let finalPan = viewportState.pan;
            if (clampedZoom !== viewportState.zoom) {
              const elemViewportRect = elemBounds.aligningTopLeftToOrigin();
              const scaledDocCenter = doc.bounds.center.mul(clampedZoom);
              finalPan = elemViewportRect.center.sub(scaledDocCenter);
            }

            // Set the initial viewport without animation
            zoom = clampedZoom;
            pan = finalPan;
          }
        }
      }

      spatialIndex = doc.makeSpatialIndex();
      const context = new ScriptingContext(commandContext());
      setGlobalScriptingEnv(context);
    }
  }

  // Removed app-level locale sync; locale now document-scoped via commands.

  // Bind locale command (locale value supplied via Commands.setPendingLocale before dispatch)
  commandManager.bind(CommandId.SET_LOCALE, () => {
    const ctx = commandContext();
    Commands.setLocale(ctx);
    ctx.commit();
  });

  // #endregion
</script>

<!-- #endregion -->

<!-- #region Styles -->

<style>
  .v-stack {
    display: flex;
    flex-direction: column;
  }

  .h-stack {
    display: flex;
    flex-direction: row;
  }

  .grow-item {
    position: relative;
    flex-grow: 1;
  }

  .z-stack {
    position: relative;
  }
</style>

<div bind:this={element} id="DocView" style="width: 100%; height: 100%;" class="v-stack">
  <BarContainer
    orientation="horizontal"
    vBarSide={VAlignment.TOP}
    bind:visible={prefs.topBarVisible}
  >
    <TopBar bind:doc bind:selectedGroupClass />
  </BarContainer>
  <div id="MiddleRow" class="grow-item h-stack">
    <BarContainer barSide={HAlignment.LEADING} bind:visible={prefs.leftBarVisible}>
      <LeftBar
        {commandManager}
        {toolManager}
        {undoManager}
        bind:doc
        bind:selectedUnitClass
      ></LeftBar>
    </BarContainer>
    <div id="ZStack" class="grow-item z-stack">
      <DocCanvas
        {assignUp}
        {commandManager}
        {isModal}
        {keyManager}
        {layouter}
        {maxZoom}
        {minZoom}
        {selectedGroupClass}
        {selectedUnitClass}
        {toolManager}
        {transitionManager}
        {undoManager}
        bind:entryPoints
        bind:zoom
        bind:doc
        bind:selection
        bind:lastSelection
        bind:spatialIndex
        bind:targetZoom
        bind:pan
        bind:targetPan
        bind:titleEditorState
      />
      <Hud {keyManager}></Hud>
    </div>
    <BarContainer barSide={HAlignment.TRAILING} bind:visible={prefs.rightBarVisible}>
      <RightBar {keyManager} />
    </BarContainer>
  </div>
  <BarContainer
    orientation="horizontal"
    vBarSide={VAlignment.BOTTOM}
    bind:visible={prefs.bottomBarVisible}
  >
    <BottomBar {commandManager} {maxZoom} {minZoom} bind:zoom bind:pan />
  </BarContainer>
</div>
<TitleEditor bind:titleEditorState></TitleEditor>
